/**
 * Main-thread client for the engine worker. Owns the single worker instance, correlates
 * request/response by id, seeds each request with a storage snapshot from the LIVE backend,
 * and reconciles the returned snapshot back into localStorage when the op resolves.
 *
 * Engine ops mutate the profile/run objects inside the worker and return the updated copies;
 * the caller swaps its React state to those returned objects.
 */

import { getStorage } from "../lib/storage";
import {
  reconcileStorage,
  snapshotStorage,
  storagePrefixesForUser,
  type StorageSnapshot,
  type WorkerOp,
  type WorkerRequest,
  type WorkerResponse,
} from "./protocol";

export interface EngineResult {
  user_profile: Record<string, any>;
  run_data: Record<string, any>;
  result: any;
}

interface Pending {
  resolve: (r: EngineResult) => void;
  reject: (e: Error) => void;
  onProgress?: (message: string, fraction?: number) => void;
  before: StorageSnapshot;
  prefixes: string[];
  /** Display-only op: skip the storage reconcile on resolve (see runEngineOp). */
  skipReconcile?: boolean;
}

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<number, Pending>();

function ensureWorker(): Worker {
  if (worker === null) {
    worker = new Worker(new URL("./engine.worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
      const msg = ev.data;
      const entry = pending.get(msg.id);
      if (!entry) return;
      if (msg.kind === "progress") {
        entry.onProgress?.(msg.message, msg.fraction);
        return;
      }
      pending.delete(msg.id);
      if (msg.kind === "failure") {
        entry.reject(new Error(msg.error));
        return;
      }
      // Reconcile storage the worker wrote back into the live backend — but NOT for
      // display-only ops: their `before` snapshot may predate a concurrent WRITE op's
      // results (the heatmap fires psychology/breakdown reads while a candidate scan is
      // running), and reconciling that stale view would delete the scan's stored times.
      if (!entry.skipReconcile) {
        reconcileStorage(getStorage(), entry.before, msg.storage, entry.prefixes);
      }
      entry.resolve({
        user_profile: msg.user_profile,
        run_data: msg.run_data,
        result: msg.result,
      });
    };
    worker.onerror = (ev) => {
      const err = new Error(ev.message || "Engine worker crashed");
      for (const [id, entry] of pending) {
        pending.delete(id);
        entry.reject(err);
      }
      // A crashed worker never answers again — tear it down and reset the handle so the
      // next op spawns a fresh worker instead of posting into a dead one and hanging.
      worker?.terminate();
      worker = null;
    };
  }
  return worker;
}

/**
 * Run an engine op in the worker. `user_profile` and `run_data` are sent by structured clone
 * (deep-copied), so the originals are untouched; use the returned copies as the new state.
 */
export function runEngineOp(
  op: WorkerOp,
  user_profile: Record<string, any>,
  run_data: Record<string, any>,
  params?: Record<string, any>,
  onProgress?: (message: string, fraction?: number) => void,
  opts?: { skipReconcile?: boolean },
): Promise<EngineResult> {
  const w = ensureWorker();
  const id = ++seq;
  const slug = String(user_profile?.slug ?? "user");
  const prefixes = storagePrefixesForUser(slug);
  const before = snapshotStorage(getStorage(), prefixes);
  const request: WorkerRequest = { id, op, user_profile, run_data, params, storage: before };
  return new Promise<EngineResult>((resolve, reject) => {
    pending.set(id, { resolve, reject, onProgress, before, prefixes, skipReconcile: opts?.skipReconcile });
    w.postMessage(request);
  });
}

/** Tear down the worker (e.g. on logout / hot-reload). */
export function disposeEngineWorker(): void {
  if (worker !== null) {
    worker.terminate();
    worker = null;
    pending.clear();
  }
}
